import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { subDays, subMonths, subYears, formatISO, format, parseISO } from 'date-fns';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import CellVoltageChart from '../components/CellVoltageChart';
import SensorPanel from '../components/SensorPanel';
import CellHeatmap from '../components/CellHeatmap';
import StatsPanel from '../components/StatsPanel';
import EditableTitle from '../components/EditableTitle';
import InsightsPanel from '../components/InsightsPanel';
import MonthlyAvgChart from '../components/MonthlyAvgChart';

// Cell voltage chart groups — rendered dynamically based on which have data
const CELL_GROUPS = [
  { key: 'cell_a', label: 'CE-01 to CE-15' },
  { key: 'cell_b', label: 'CE-16 to CE-30' },
  { key: 'cell_c', label: 'CE-31 to CE-45' },
  { key: 'cell_d', label: 'CE-46 to CE-60' },
  { key: 'cell_e', label: 'CE-61 to CE-75' },
  { key: 'cell_f', label: 'CE-76 to CE-90' },
  { key: 'cell_g', label: 'CE-91 to CE-105' },
  { key: 'cell_h', label: 'CE-106 to CE-120' },
];

const PERIODS = [
  { label: 'Last 7 days',   value: '7d' },
  { label: 'Last 14 days',  value: '14d' },
  { label: 'Last 30 days',  value: '30d' },
  { label: 'Last 90 days',  value: '90d' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'Last year',     value: '1y' },
  { label: 'Custom range',  value: 'custom' },
];

// Default chart labels — projects can override any of these
const DEFAULT_LABELS = {
  cell_a:      'CE-01 to CE-15',
  cell_b:      'CE-16 to CE-30',
  cell_c:      'CE-31 to CE-45',
  cell_d:      'CE-46 to CE-60',
  cell_e:      'CE-61 to CE-75',
  cell_f:      'CE-76 to CE-90',
  cell_g:      'CE-91 to CE-105',
  cell_h:      'CE-106 to CE-120',
  temperature: 'Temperature',
  level:       'Tank Level',
  flow:        'Flow',
  pressure:    'Pressure',
  rectifier:   'Rectifier',
  pump:        'Pump Speed',
  ph:          'pH',
  heatmap:     'Cell Health Heatmap',
  analysis:    'Cell Analysis',
};

const today = () => formatISO(new Date(), { representation: 'date' });

// Latest sampled timestamp comes from the server as a naive, plant-local ISO
// string (the gateway stores plant-local time). Format the wall-clock value as
// given, without applying the browser's timezone.
function fmtSample(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), "MMM d, HH:mm"); } catch { return iso; }
}

function presetFrom(period) {
  const now = new Date();
  switch (period) {
    case '7d':  return formatISO(subDays(now, 7),   { representation: 'date' });
    case '14d': return formatISO(subDays(now, 14),  { representation: 'date' });
    case '30d': return formatISO(subDays(now, 30),  { representation: 'date' });
    case '90d': return formatISO(subDays(now, 90),  { representation: 'date' });
    case '6m':  return formatISO(subMonths(now, 6), { representation: 'date' });
    case '1y':  return formatISO(subYears(now, 1),  { representation: 'date' });
    default:    return formatISO(subDays(now, 90),  { representation: 'date' });
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const { currentProject } = useProject();

  const [period, setPeriod]       = useState('7d');
  const [dateFrom, setDateFrom]   = useState(() => presetFrom('7d'));
  const [dateTo, setDateTo]       = useState(() => today());
  const [uploadIds, setUploadIds] = useState([]);
  const [syncing, setSyncing]     = useState(false);
  const [dataLoading, setDataLoading]         = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [rows, setRows]           = useState([]);
  const [columns, setColumns]     = useState({
    cell_a: [], cell_b: [], cell_c: [], cell_d: [],
    cell_e: [], cell_f: [], cell_g: [], cell_h: [],
    temperature: [], level: [], flow: [], pressure: [], rectifier: [], pump: [], ph: [],
  });
  // Last non-empty columns for this project — used to keep charts visible when
  // the selected date range has no data (so empty charts show instead of nothing).
  const [knownColumns, setKnownColumns] = useState({});
  const [sidebarTab, setSidebarTab]           = useState('analytics');
  const [metricTab, setMetricTab]             = useState('voltage');
  const [cellStats, setCellStats]             = useState([]);
  const [segmentAnalysis, setSegmentAnalysis] = useState([]);
  const [resistanceRows, setResistanceRows]   = useState([]);
  const [resistanceStatus, setResistanceStatus] = useState('ok');
  const [resistanceSegmentAnalysis, setResistanceSegmentAnalysis] = useState([]);
  const [uptimePct, setUptimePct]               = useState(null);
  const [rectifierCount, setRectifierCount]     = useState(0);
  const [uptimeVThreshold, setUptimeVThreshold] = useState(() => {
    const saved = localStorage.getItem(`thresh_v_${currentProject?.id}`);
    return saved !== null ? Number(saved) : 10.0;
  });
  const [uptimeIThreshold, setUptimeIThreshold] = useState(() => {
    const saved = localStorage.getItem(`thresh_i_${currentProject?.id}`);
    return saved !== null ? Number(saved) : 1.0;
  });
  const [rowCount, setRowCount]     = useState(null);
  // Live mode: poll the telemetry store and drive the same chart states.
  const [liveAvailable, setLiveAvailable] = useState(false);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState(null);
  const [liveLatestTs, setLiveLatestTs]   = useState(null);
  const [liveTz, setLiveTz]               = useState(null);
  const [liveTick, setLiveTick]           = useState(0);
  const [labels, setLabels]         = useState(DEFAULT_LABELS);
  const fetchRef = useRef(0);

  // Sync labels from project whenever project changes
  useEffect(() => {
    if (!currentProject) return;
    setLabels({ ...DEFAULT_LABELS, ...(currentProject.chart_labels || {}) });
  }, [currentProject]);

  // Load saved thresholds when project changes
  useEffect(() => {
    if (!currentProject?.id) return;
    const v = localStorage.getItem(`thresh_v_${currentProject.id}`);
    const i = localStorage.getItem(`thresh_i_${currentProject.id}`);
    setUptimeVThreshold(v !== null ? Number(v) : 10.0);
    setUptimeIThreshold(i !== null ? Number(i) : 1.0);
  }, [currentProject?.id]);

  // Admin: save a single label update
  const saveLabel = useCallback(async (key, value) => {
    if (!currentProject) return;
    const next = { ...(currentProject.chart_labels || {}), [key]: value };
    setLabels(prev => ({ ...prev, [key]: value }));
    try {
      await api.patch(`/api/projects/${currentProject.id}/labels`, next);
      // Update the project in context so it persists across re-renders
      currentProject.chart_labels = next;
    } catch { /* revert on failure */ setLabels(prev => ({ ...prev })); }
  }, [currentProject]);

  const mkTitle = useCallback((key) => (
    <EditableTitle
      value={labels[key] || DEFAULT_LABELS[key]}
      onSave={user?.is_admin ? (v) => saveLabel(key, v) : undefined}
    />
  ), [labels, user, saveLabel]);

  // Fetch uploads for the current project
  const refreshUploadIds = useCallback(async () => {
    if (!currentProject) return;
    try {
      const res = await api.get('/api/uploads', { params: { project_id: currentProject.id } });
      setUploadIds(res.data.map(u => u.id));
    } catch { /* silent */ }
  }, [currentProject]);

  // Reload uploads whenever the project changes; clear data immediately
  useEffect(() => {
    setRows([]);
    setColumns({
      cell_a: [], cell_b: [], cell_c: [], cell_d: [],
      cell_e: [], cell_f: [], cell_g: [], cell_h: [],
      temperature: [], level: [], flow: [], pressure: [], rectifier: [], pump: [], ph: [],
    });
    setKnownColumns({});
    setCellStats([]); setSegmentAnalysis([]); setResistanceRows([]); setResistanceStatus('ok'); setResistanceSegmentAnalysis([]); setUptimePct(null); setRowCount(null);
    setUploadIds([]);
    refreshUploadIds();
  }, [refreshUploadIds]);

  // Preset dropdown changed
  const handlePeriodChange = (val) => {
    setPeriod(val);
    if (val !== 'custom') {
      setDateFrom(presetFrom(val));
      setDateTo(today());
    }
  };

  // Manual date edit → switch to custom
  const handleDateFromChange = (val) => { setDateFrom(val); setPeriod('custom'); };
  const handleDateToChange   = (val) => { setDateTo(val);   setPeriod('custom'); };

  // Chart brush selection → update dates
  const handleBrushChange = useCallback((startTs, endTs) => {
    if (!startTs || !endTs) return;
    setDateFrom(startTs.slice(0, 10));
    setDateTo(endTs.slice(0, 10));
    setPeriod('custom');
  }, []);

  // Fetch chart data + analysis in one request
  useEffect(() => {
    if (!uploadIds.length) {
      setRows([]); setRowCount(null);
      setCellStats([]); setSegmentAnalysis([]); setUptimePct(null);
      return;
    }
    const ticket = ++fetchRef.current;
    setDataLoading(true);
    setAnalysisLoading(true);
    api.post('/api/data', {
      upload_ids: uploadIds, date_from: dateFrom, date_to: dateTo,
      uptime_v_threshold: uptimeVThreshold, uptime_i_threshold: uptimeIThreshold,
      project_id: currentProject?.id,
    })
      .then(res => {
        if (ticket !== fetchRef.current) return;
        const d = res.data;
        setRows(d.rows || []);
        setResistanceRows(d.resistance_rows || []);
        setResistanceStatus(d.resistance_status || 'ok');
        const cols = d.columns || {};
        setColumns(cols);
        if (Object.values(cols).some(arr => arr.length > 0)) setKnownColumns(cols);
        setRowCount(d.row_count ?? null);
        setCellStats(d.cell_stats || []);
        setSegmentAnalysis(d.segment_analysis || []);
        setResistanceSegmentAnalysis(d.resistance_segment_analysis || []);
        setUptimePct(d.uptime_pct ?? null);
        setRectifierCount(d.rectifier_count || 0);
        if (d.live) { setLiveAvailable(true); setLiveUpdatedAt(new Date()); setLiveLatestTs(d.date_to || null); setLiveTz(d.tz || null); }
      })
      .catch(() => {})
      .finally(() => {
        if (ticket === fetchRef.current) {
          setDataLoading(false);
          setAnalysisLoading(false);
        }
      });
  }, [uploadIds, dateFrom, dateTo, uptimeVThreshold, uptimeIThreshold, liveTick, currentProject?.id]);

  // Reset the live badge on project switch; /api/data reports whether this
  // project has a gateway (d.live), so no separate probe is needed.
  useEffect(() => {
    setLiveAvailable(false); setLiveUpdatedAt(null);
  }, [currentProject?.id]);

  // When a gateway is linked, tick every 60s. The /api/data effect depends on
  // liveTick, so each tick refetches — pulling in the newly-merged live tail.
  useEffect(() => {
    if (!liveAvailable) return undefined;
    const id = setInterval(() => setLiveTick(t => t + 1), 300000);  // 5 min — data lands every 10
    return () => clearInterval(id);
  }, [liveAvailable]);


  // The sync endpoint returns as soon as the work is queued, and the 2-hourly
  // scheduler can start one nobody clicked. Poll the project's sync state so the
  // button spins for the real duration and the charts reload the moment new
  // files land — fast while a sync is live, lazily otherwise.
  const wasSyncingRef = useRef(false);

  const pollSyncStatus = useCallback(async () => {
    if (!currentProject) return;
    try {
      const { data } = await api.get(`/api/projects/${currentProject.id}/sync-status`);
      setSyncing(data.running);
      if (wasSyncingRef.current && !data.running && data.synced > 0) refreshUploadIds();
      wasSyncingRef.current = data.running;
    } catch { /* silent — sync status is advisory */ }
  }, [currentProject, refreshUploadIds]);

  useEffect(() => {
    if (!currentProject) return undefined;
    pollSyncStatus();
    const id = setInterval(pollSyncStatus, syncing ? 2000 : 20000);
    return () => clearInterval(id);
  }, [currentProject, syncing, pollSyncStatus]);

  const handleSyncS3 = useCallback(async () => {
    if (!currentProject) return;
    setSyncing(true);          // poll takes over from here
    wasSyncingRef.current = true;
    try {
      await api.post(`/api/projects/${currentProject.id}/sync-s3`);
    } catch {
      setSyncing(false);
      wasSyncingRef.current = false;
    }
  }, [currentProject]);

  // Merge resistance values into the full timestamp series so the resistance
  // chart shares the same X axis as the voltage chart.
  // Timestamps where the rectifier was off have no cell values → gaps in the lines.
  // If resistanceRows is completely empty (no current column in data at all),
  // keep it empty so the chart shows "no data" rather than an all-shaded blank chart.
  const mergedResistanceRows = useMemo(() => {
    if (!rows.length || !resistanceRows.length) return resistanceRows;
    const rMap = new Map(resistanceRows.map(r => [r.t, r]));
    return rows.map(r => rMap.get(r.t) ?? { t: r.t });
  }, [rows, resistanceRows]);

  const loading = dataLoading || analysisLoading;

  return (
    <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>

      {/* ── Left nav sidebar ── */}
      <div style={{
        width: 172,
        flexShrink: 0,
        position: 'sticky',
        top: 20,
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}>
        {[
          { key: 'analytics', icon: '📊', label: 'Analytics' },
          { key: 'extra',     icon: '📈', label: 'Additional Analytics' },
        ].map(({ key, icon, label }) => (
          <button
            key={key}
            onClick={() => setSidebarTab(key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', textAlign: 'left',
              padding: '9px 12px',
              borderRadius: 8,
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: sidebarTab === key ? 700 : 500,
              background: sidebarTab === key ? '#e8eef8' : 'transparent',
              color: sidebarTab === key ? '#1e3a6e' : '#5a6a80',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            <span style={{ fontSize: 14 }}>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

      {sidebarTab === 'extra' ? (
        /* ── Additional Analytics panel ── */
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#142D55', marginBottom: 16 }}>
            Additional Analytics
          </div>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Monthly Average Cell Voltage</span>
              <span style={{ fontSize: 11, color: '#6b7a8d' }}>All uploaded data · running rows only</span>
            </div>
            <div style={{ padding: '16px 20px 20px' }}>
              <MonthlyAvgChart projectId={currentProject?.id} />
            </div>
          </div>
        </div>
      ) : (
      <>
      <InsightsPanel
        projectId={currentProject?.id}
        uptimeVThreshold={uptimeVThreshold}
        uptimeIThreshold={uptimeIThreshold}
      />

      {/* ── Controls bar ── */}
      <div className="controls-bar">
        {/* Live badge — shown when a gateway is linked; the view auto-refreshes
            every 60s and the file+live data are already merged server-side. */}
        {liveAvailable && (
          <div
            title="Live gateway connected. This view refreshes every 60 seconds."
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid #1f9d55', background: '#e9f9ef',
              color: '#137a3e', fontSize: 12, fontWeight: 600,
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: '#1f9d55',
              animation: 'livepulse 1.6s ease-in-out infinite',
            }} />
            LIVE
            {liveLatestTs && (
              <span style={{ fontWeight: 400, opacity: 0.75 }}>
                · last sample {fmtSample(liveLatestTs)}{liveTz ? ` ${liveTz}` : ''}
              </span>
            )}
          </div>
        )}

        {/* Period preset */}
        <div className="period-select">
          <span className="period-label">Show:</span>
          <select value={period} onChange={e => handlePeriodChange(e.target.value)}>
            {PERIODS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        {/* Date range inputs */}
        <div className="date-inputs">
          <input
            type="date"
            value={dateFrom}
            onChange={e => handleDateFromChange(e.target.value)}
            className="date-input-inline"
          />
          <span className="date-sep">→</span>
          <input
            type="date"
            value={dateTo}
            onChange={e => handleDateToChange(e.target.value)}
            className="date-input-inline"
          />
        </div>

        {/* Rectifier thresholds — filter downtime rows from all calculations */}
        <div className="threshold-group">
          <span className="threshold-label">Running if:</span>
          <label className="threshold-field">
            <span>V &gt;</span>
            <input
              type="number"
              className="threshold-input"
              value={uptimeVThreshold}
              min={0}
              step={1}
              onChange={e => {
                const v = Number(e.target.value);
                setUptimeVThreshold(v);
                if (currentProject?.id) localStorage.setItem(`thresh_v_${currentProject.id}`, v);
              }}
            />
            <span>V</span>
          </label>
          <label className="threshold-field">
            <span>I &gt;</span>
            <input
              type="number"
              className="threshold-input"
              value={uptimeIThreshold}
              min={0}
              step={0.1}
              onChange={e => {
                const v = Number(e.target.value);
                setUptimeIThreshold(v);
                if (currentProject?.id) localStorage.setItem(`thresh_i_${currentProject.id}`, v);
              }}
            />
            <span>A</span>
          </label>
        </div>

        {/* Summary */}
        {rowCount != null && (
          <span className="data-summary">
            {rowCount.toLocaleString()} readings
            {loading && <span className="loading-dot"> · loading…</span>}
          </span>
        )}
        {uploadIds.length === 0 && !loading && currentProject && (
          <span className="data-summary">No data for {currentProject.name}</span>
        )}

        <div style={{ flex: 1 }} />

        {/* Admin-only manual S3 sync */}
        {user?.is_admin && currentProject?.s3_prefix && (
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12 }}
            onClick={handleSyncS3}
            disabled={syncing}
            title={syncing ? 'Sync in progress — charts reload when it finishes' : 'Pull new files from S3'}
          >
            <span className={`sync-glyph${syncing ? ' is-spinning' : ''}`}>{syncing ? '↻' : '☁'}</span> Sync S3
          </button>
        )}
      </div>

      {/* ── Voltage / Resistance tab switcher ── */}
      <div style={{ display: 'flex', gap: 6, margin: '18px 0 2px' }}>
        <button
          className={`btn btn-sm ${metricTab === 'voltage' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMetricTab('voltage')}
        >
          ⚡ Voltage
        </button>
        <button
          className={`btn btn-sm ${metricTab === 'resistance' ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setMetricTab('resistance')}
        >
          Ω Resistance
        </button>
        {metricTab === 'resistance' && (
          <span style={{ fontSize: 12, color: '#6b7a8d', alignSelf: 'center', marginLeft: 6 }}>
            R = V / I · calculated per cell per timestamp · units: mΩ
          </span>
        )}
      </div>

      {/* ── Cell charts — voltage or resistance ── */}
      {(() => {
        const isResistance = metricTab === 'resistance';
        const chartRows = isResistance ? mergedResistanceRows : rows;

        // When on resistance tab but data is unavailable, show one clear reason
        // instead of N individual "No data loaded" chart cards.
        if (isResistance && rows.length > 0 && mergedResistanceRows.length === 0) {
          const messages = {
            no_current_column:  { icon: '⚠️', text: 'No current (A) column found in this project\'s data. Add a rectifier current measurement to the column mapping to enable resistance calculation.' },
            current_all_zero:   { icon: '⚠️', text: 'Rectifier current reads 0 A for all timestamps — R = V / I cannot be computed. Check that the current sensor is connected and the data export includes live readings.' },
            insufficient_data:  { icon: 'ℹ️', text: 'Not enough running-condition rows to compute resistance. Ensure the date range includes periods when the rectifier was active.' },
          };
          const msg = messages[resistanceStatus] || { icon: 'ℹ️', text: 'Resistance data is not available for the selected date range.' };
          return (
            <div style={{
              margin: '12px 0',
              padding: '18px 22px',
              background: 'rgba(230,130,0,0.08)',
              border: '1px solid rgba(230,130,0,0.30)',
              borderRadius: 10,
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              fontSize: 13,
              color: '#5a3800',
            }}>
              <span style={{ fontSize: 20, lineHeight: 1.3 }}>{msg.icon}</span>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Resistance unavailable</div>
                <div style={{ lineHeight: 1.6 }}>{msg.text}</div>
              </div>
            </div>
          );
        }

        const displayCols = Object.values(columns).some(a => a.length > 0) ? columns : knownColumns;
        const active = CELL_GROUPS.filter(g => (displayCols[g.key] || []).length > 0);
        return (
          <div className="charts-section">
            {active.map(g => (
              <div key={g.key} className="chart-wrap">
                {dataLoading && <div className="loading-overlay"><div className="spinner" /></div>}
                <CellVoltageChart
                  rows={chartRows}
                  columns={displayCols[g.key] || []}
                  title={mkTitle(g.key)}
                  onBrushChange={handleBrushChange}
                  unit={isResistance ? 'mΩ' : 'V'}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                />
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Sensor panels (voltage tab only) ── */}
      {metricTab === 'voltage' && (
        <div className="sensor-grid">
          <SensorPanel rows={rows} columns={columns.temperature || []} title={mkTitle('temperature')} unit="°C"     dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.level       || []} title={mkTitle('level')}       unit="%"     dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.flow        || []} title={mkTitle('flow')}        unit="m³/hr" dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.pressure    || []} title={mkTitle('pressure')}    unit="Bar"   dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.rectifier   || []} title={mkTitle('rectifier')}               dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.pump        || []} title={mkTitle('pump')}        unit="%"     dateFrom={dateFrom} dateTo={dateTo} />
          <SensorPanel rows={rows} columns={columns.ph          || []} title={mkTitle('ph')}          unit=""      dateFrom={dateFrom} dateTo={dateTo} />
        </div>
      )}

      {/* ── Analysis ── */}
      <div className="analysis-grid">
        <div className="chart-wrap">
          {analysisLoading && <div className="loading-overlay"><div className="spinner" /></div>}
          <CellHeatmap
            segmentAnalysis={metricTab === 'voltage' ? segmentAnalysis : resistanceSegmentAnalysis}
            labels={labels}
            uptimePct={uptimePct}
            rectifierCount={rectifierCount}
            uptimeVThreshold={uptimeVThreshold}
            uptimeIThreshold={uptimeIThreshold}
            title={mkTitle('heatmap')}
            mode={metricTab}
          />
        </div>
        <div className="chart-wrap">
          {analysisLoading && <div className="loading-overlay"><div className="spinner" /></div>}
          <StatsPanel
            segmentAnalysis={metricTab === 'voltage' ? segmentAnalysis : resistanceSegmentAnalysis}
            labels={labels}
            title={mkTitle('analysis')}
            mode={metricTab}
            rectifierCount={rectifierCount}
          />
        </div>
      </div>

      </>
      )}
      </div>
    </div>
  );
}
